import { queryOptions, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  type Action,
  Alert,
  Badge,
  Icon,
  backLinkClasses,
  linkTextClasses,
  NotProvided,
  Table,
  Page,
  PageHeader,
} from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import type { AwardState, Period, WalletRow } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  AWARD_STALE_MS,
  ReceiptMonthStrip,
  StateSwitch,
  awardPeriod,
  periodLabel,
} from './common.js';
import {
  WalletBoletos,
  WalletDistribution,
  WalletEvolution,
  WalletOrigin,
  WalletValues,
  WalletVehicles,
  measuredInsights,
  measuredOpenBoletos,
  sideTotal,
} from './WalletInsights.js';

/**
 * A CARTEIRA — gráficos e métricas: quanto ela rende, de onde vem, como
 * evoluiu dia a dia, como estão os boletos e o que ela tem.
 * O desenho é o das referências de 29/10/2026 — ver `WalletInsights.tsx`.
 *
 * ## O detalhe mora no Painel de Gestão (20/10/2026)
 *
 * As premiações uma a uma, pelos dois lados ("Você já garantiu" e "Você está
 * perdendo"), o resumo da carteira e o envio da nota fiscal são do painel do
 * consultor (`ConsultantPanelScreen`). Nesse mesmo dia elas passaram algumas
 * horas aqui, e o dono pediu a divisão de volta: a Carteira é o que se lê de
 * relance, o painel é o que se confere. As duas telas linkam uma para a outra.
 *
 * ## Os dois lados, sempre, e nenhum seletor
 *
 * Os Valores apurados somam os dois lados no "total geral apurado", com o
 * rodapé "se todos pagarem" — é a única soma deles que a casa faz, e o rótulo
 * é o que a torna uma afirmação:
 * um lado é recebido e o outro é hipótese. Por isso a tela pede os DOIS lados
 * sempre, e o `state` do endereço não muda nada aqui; ele só viaja para o
 * painel e para a tabela de todas as carteiras, que mostram um de cada vez.
 *
 * O valor do lado perdido é BRUTO: a tarifa do banco nasce no pagamento, e um
 * boleto em aberto não tem nenhuma.
 */
export function WalletScreen() {
  const search = useSearch({ from: '/authenticated/sales/metrics' });
  const { data: context } = useSessionContext();

  /*
   * O DESPACHO entre as duas leituras, e ele mora aqui — não no menu.
   *
   * Com `ver_carteiras` e sem ninguém escolhido, a tela é a tabela de todas as
   * carteiras; com `consultant` no endereço, é a carteira daquela pessoa. Quem
   * tem só `ver` nunca vê a tabela e continua na própria, como sempre. Dois
   * itens de menu com o mesmo rótulo fariam o menu perguntar o que a
   * permissão já respondeu — o mesmo motivo do Painel de Gestão.
   */
  if (
    can(context, 'sales.management', 'ver_carteiras') &&
    search.consultant === undefined
  ) {
    return <AllWallets state={search.state} initialMonth={search.month} />;
  }

  return (
    <OneWallet
      state={search.state}
      consultant={search.consultant}
      initialMonth={search.month}
    />
  );
}

/** Uma carteira — a de quem está logado, ou a de `consultant`. */
function OneWallet({
  state,
  consultant,
  initialMonth,
}: {
  state: AwardState;
  consultant: string | undefined;
  initialMonth: string | undefined;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const fromList = can(context, 'sales.management', 'ver_carteiras');

  /*
   * O LADO mora no endereço e a COMPETÊNCIA mora no componente, e a assimetria
   * é deliberada: o lado é o que se manda para alguém ("olha o que estamos
   * perdendo"), então o link precisa carregá-lo. O mês viaja junto no link da
   * premiação, que é onde a conferência acontece de fato — e no clique da
   * tabela de todas as carteiras, que é de onde ele chega como `initialMonth`.
   */
  const [period, setPeriod] = useState<Period>(
    (initialMonth as Period | undefined) ?? awardPeriod(),
  );

  /* Os DOIS lados, sempre: a previsão precisa dos dois. */
  const guaranteedQuery = useQuery(
    walletSideQuery(tenantId, period, 'garantido', consultant),
  );
  const atRiskQuery = useQuery(walletSideQuery(tenantId, period, 'perdendo', consultant));
  /*
   * O nome, a recusa (`sem_vinculo`) e o erro vêm do lado
   * garantido: os dois respondem o mesmo estado, e o garantido é o padrão.
   */
  const query = guaranteedQuery;

  const insightsQuery = useQuery({
    queryKey: ['sales', 'wallet', 'insights', tenantId, period, consultant],
    queryFn: async () => {
      const r = await api.sales.walletInsights({ query: { month: period, consultant } });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a evolução da carteira'));
      }
      return r.body;
    },
    staleTime: AWARD_STALE_MS,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? previous : undefined,
  });
  const insights = measuredInsights(insightsQuery.data);

  const guaranteed =
    guaranteedQuery.data?.estado === 'medido' ? guaranteedQuery.data.award : undefined;
  const atRisk =
    atRiskQuery.data?.estado === 'medido' ? atRiskQuery.data.award : undefined;

  /*
   * TODOS os boletos em aberto da competência, sem repetição — o Aberto e o
   * Vencido dos Boletos. A tabela deles saiu daqui em 29/10/2026: o atraso é
   * da Carteira.
   */
  const openQuery = useQuery({
    queryKey: ['sales', 'wallet', 'open-boletos', tenantId, period, consultant],
    queryFn: async () => {
      const r = await api.sales.walletOpenBoletos({
        query: { month: period, consultant },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os boletos em aberto'));
      }
      return r.body;
    },
    staleTime: AWARD_STALE_MS,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? previous : undefined,
  });
  const openBoletos = measuredOpenBoletos(openQuery.data)?.items;

  /*
   * Trocar de mês mantém o mês velho na tela (`placeholderData`) até o novo
   * chegar — e ele aparecia sem sinal nenhum de carregamento. Com isto os
   * blocos voltam ao esqueleto, e a faixa marca o mês como ocupado.
   */
  const switching = [guaranteedQuery, atRiskQuery, insightsQuery, openQuery].some(
    (q) => q.isPlaceholderData,
  );

  const panel = query.data;

  return (
    <Page measure="cheia">
      {consultant !== undefined && fromList && (
        <Link
          to="/sales/metrics"
          search={{ state, month: period }}
          className={backLinkClasses()}
        >
          <Icon nome="seta-esquerda" size={16} />
          Todos os consultores
        </Link>
      )}
      <PageHeader
        title={
          consultant === undefined
            ? 'BI'
            : `BI de ${
                panel?.estado === 'medido' && panel.consultant.nome !== null
                  ? panel.consultant.nome
                  : `consultor ${consultant}`
              }`
        }
        actions={
          /* O detalhe — premiação por premiação e a nota fiscal — é do Início. */
          <Link
            to="/sales/home"
            search={{ consultant, month: period }}
            className={linkTextClasses()}
          >
            Ver as premiações no Início
          </Link>
        }
      >
        Visão analítica da carteira e das premiações — {periodLabel(period)}.
      </PageHeader>

      {/*
       * A barra fica ACIMA do conteúdo e fora de qualquer cartão, porque o que
       * ela governa é a tela inteira — o lado e a competência mudam todos os
       * números abaixo. Dentro de um cartão, pareceria filtro daquele bloco.
       */}
      <ReceiptMonthStrip current={period} onChange={setPeriod} loading={switching} />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {insightsQuery.isError && <Alert tone="erro">{insightsQuery.error.message}</Alert>}
      {openQuery.isError && <Alert tone="erro">{openQuery.error.message}</Alert>}

      {/*
       * A recusa vem NO LUGAR do conteúdo, e não acima dele — o mesmo desenho
       * do painel. Uma carteira zerada embaixo de um aviso diria "você não tem
       * nada" logo depois de dizer que não sabe, e a afirmação é a que se lê.
       */}
      {panel?.estado === 'sem_vinculo' && <Alert tone="alerta">{panel.message}</Alert>}

      {/*
       * O conteúdo desenha também enquanto a PRIMEIRA leitura chega, com os
       * esqueletos de cada bloco (29/10/2026): antes a tela ficava em branco
       * abaixo da faixa até a apuração responder. A recusa e o erro continuam
       * no lugar dele.
       */}
      {(panel?.estado === 'medido' || (query.isPending && !query.isError)) && (
        <>
          <WalletValues
            guaranteed={sideTotal(guaranteed?.categories)}
            atRisk={sideTotal(atRisk?.categories)}
            ticket={insights?.ticket}
            month={period}
            loading={busy(guaranteedQuery) || busy(atRiskQuery)}
            ticketLoading={busy(insightsQuery)}
          />

          <WalletEvolution
            insights={insights}
            month={period}
            loading={busy(insightsQuery)}
            error={insightsQuery.error}
          />

          <WalletOrigin
            guaranteed={guaranteed?.categories.flatMap((c) => c.lines) ?? []}
            atRisk={atRisk?.categories.flatMap((c) => c.lines) ?? []}
            loading={busy(guaranteedQuery) || busy(atRiskQuery)}
          />

          <WalletBoletos
            ticket={insights?.ticket}
            openBoletos={openBoletos}
            loading={busy(insightsQuery) || busy(openQuery)}
          />

          <WalletVehicles
            insights={insights}
            openBoletos={openBoletos}
            loading={busy(insightsQuery) || busy(openQuery)}
          />

          <WalletDistribution mix={insights?.mix} loading={busy(insightsQuery)} />
        </>
      )}
    </Page>
  );
}

/**
 * Um bloco está CARREGANDO: a primeira leitura, ou o mês novo chegando com o
 * velho ainda na tela (`placeholderData`) — os dois desenham o esqueleto.
 */
function busy(q: { isLoading: boolean; isPlaceholderData: boolean }): boolean {
  return q.isLoading || q.isPlaceholderData;
}

/**
 * A consulta de UM lado da carteira — `garantido` ou `perdendo`.
 *
 * Trocar de praça descarta o que estava na tela, pelo motivo do painel: o
 * código do voluntário muda por base — 394 no CE e 75 no GO são a mesma
 * pessoa —, e manter o anterior desenharia a carteira de uma praça sob o nome
 * da outra.
 */
function walletSideQuery(
  tenantId: number | undefined,
  period: Period,
  side: AwardState,
  consultant: string | undefined,
) {
  return queryOptions({
    queryKey: ['sales', 'wallet', tenantId, period, side, consultant],
    queryFn: async () => {
      const r = await api.sales.managementPanel({
        query: { month: period, state: side, consultant },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'a carteira'));
      return r.body;
    },
    staleTime: AWARD_STALE_MS,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/**
 * TODAS AS CARTEIRAS — uma linha por consultor que tem valor na competência.
 *
 * `sales.management:ver_carteiras`, desde 25/09/2026. O total de cada linha é
 * o da carteira que o clique abre, calculado pela mesma apuração no servidor —
 * uma soma mais barata sobre o extrato gravado daria outro número, e a tabela
 * discordaria da carteira aberta a partir dela.
 *
 * **Quem recusou fica na tabela, sem total.** Tirá-lo diria que ele não tem
 * nada, quando a casa é que não sabe; o motivo está na carteira dele. Quem somou
 * exatamente R$ 0,00 sai, e a tela diz quantos são.
 */
function AllWallets({
  state,
  initialMonth,
}: {
  state: AwardState;
  initialMonth: string | undefined;
}) {
  const navigate = useNavigate();
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const [period, setPeriod] = useState<Period>(
    (initialMonth as Period | undefined) ?? awardPeriod(),
  );

  const query = useQuery({
    queryKey: ['sales', 'wallets', tenantId, period, state],
    queryFn: async () => {
      const r = await api.sales.wallets({ query: { month: period, state } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as carteiras'));
      return r.body;
    },
    staleTime: AWARD_STALE_MS,
    /* A troca de praça descarta, pelo motivo da carteira: o código é por base. */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const data = query.data;
  const open = (row: WalletRow) =>
    void navigate({
      to: '/sales/metrics',
      search: { state, consultant: row.consultant.erpVolunteerCode, month: period },
    });

  return (
    <Page measure="cheia">
      <PageHeader title="BI">
        {state === 'garantido'
          ? 'O que os boletos pagos já garantiram na carteira de cada consultor da base, na competência.'
          : 'O que os boletos em aberto ainda poderiam gerar na carteira de cada consultor da base, e não geraram.'}
      </PageHeader>

      {/* A faixa ocupa a largura inteira — doze colunas não dividem a linha. */}
      <ReceiptMonthStrip
        current={period}
        onChange={setPeriod}
        loading={query.isPlaceholderData}
      />
      <div className="flex justify-end">
        <StateSwitch
          current={state}
          own={false}
          onChange={(next) =>
            void navigate({
              to: '/sales/metrics',
              search: { state: next, month: period },
            })
          }
        />
      </div>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      <Table
        framed
        paginate={false}
        label="BI dos consultores da base"
        items={query.isPlaceholderData ? [] : (data?.rows ?? [])}
        loading={query.isLoading || query.isPlaceholderData}
        rowKey={(r) => String(r.consultant.id)}
        empty={`Nenhuma carteira com valor em ${periodLabel(period)}.`}
        rowTitle={(r) =>
          r.consultant.nome ?? `Consultor ${r.consultant.erpVolunteerCode}`
        }
        rowSubtitle={(r) => `Código ${r.consultant.erpVolunteerCode} nesta base`}
        rowIcon="cracha"
        rowActions={(r): Action[] => [
          /*
           * As premiações primeiro: o número da linha é o total delas, e é no
           * Início da pessoa que ele se confere premiação por premiação.
           */
          {
            label: 'Ver as premiações',
            icon: 'documento',
            run: () =>
              void navigate({
                to: '/sales/home',
                search: {
                  consultant: r.consultant.erpVolunteerCode,
                  month: period,
                },
              }),
          },
          { label: 'Ver o BI', icon: 'olho', run: () => open(r) },
        ]}
        columns={[
          {
            title: 'Consultor',
            alignment: 'inicio' as const,
            minWidth: '14rem',
            cell: (r) => r.consultant.nome ?? <NotProvided>sem nome</NotProvided>,
          },
          {
            title: 'Código',
            minWidth: '5.5rem',
            cell: (r) => r.consultant.erpVolunteerCode,
          },
          {
            title: 'Premiações',
            alignment: 'fim' as const,
            minWidth: '6rem',
            cell: (r) =>
              r.refused === 0 ? (
                r.awards
              ) : (
                <span title="o motivo está na carteira dele">
                  {r.awards} <span className="text-alerta">({r.refused} sem apurar)</span>
                </span>
              ),
          },
          {
            title: state === 'garantido' ? 'Boletos pagos' : 'Boletos em aberto',
            alignment: 'fim' as const,
            minWidth: '6rem',
            cell: (r) => r.boletos.toLocaleString('pt-BR'),
          },
          {
            title: state === 'garantido' ? 'Total garantido' : 'Total em risco',
            alignment: 'fim' as const,
            minWidth: '8rem',
            cell: (r) =>
              r.total === null ? (
                <Badge tone="alerta">sem total</Badge>
              ) : (
                <MoneyValue value={r.total} />
              ),
          },
        ]}
      />
    </Page>
  );
}
